import { useNavigate } from "react-router-dom";
import { ArrowLeft, Check } from "lucide-react";

import { Button } from "@/components/ui/button";
import type { GenieTemplate } from "./data";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";
import type { GenieIconName } from "@/components/ui/genie-icons/genie-icons";

interface KickstartViewProps {
  template: GenieTemplate;
  onBack: () => void;
  onDone: () => void;
}

// Preview / confirm screen after picking a Genie from the catalogue.
//
// Shows the chosen Genie's mascot, full description, and the jobs it does for
// the user. "Deploy this Genie" navigates to /user-hub/genie-setup?template=
// <slug>, where useTemplatePreload.ts loads the play from playbookGoals[],
// dispatches the full templateConfig into setup state, and the user lands in
// the existing genie-creation flow with everything pre-filled.
export const KickstartView = ({
  template,
  onBack,
}: KickstartViewProps) => {
  const navigate = useNavigate();

  const handleDeploy = () => {
    navigate(`/user-hub/genie-setup?template=${template.slug}`);
  };

  return (
    <div className="min-h-screen w-full bg-[#F9FAFC] flex flex-col">
      <div className="px-4 pt-3 md:px-6 md:pt-4">
        <Button
          onClick={onBack}
          variant="ghost"
          size="sm"
          className="text-genie-text-sub px-2 md:px-3 py-1 md:py-2 text-sm min-w-0 h-8 md:h-9 flex items-center gap-1 md:gap-2 transition-colors duration-200 rounded-md hover:text-white hover:bg-gradient-to-r hover:from-[#4E9CFF] hover:to-[#FF4DB8]"
        >
          <ArrowLeft size={16} />
          <span className="hidden md:inline">Back to catalogue</span>
        </Button>
      </div>

      <div className="flex-1 px-4 md:px-10 pb-16 pt-2 max-w-6xl mx-auto w-full">
        <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-8 md:gap-12 items-start">
          {/* Mascot column */}
          <div className="flex flex-col items-center md:items-start">
            <div className="w-44 h-44 md:w-60 md:h-60 rounded-3xl bg-gradient-to-br from-[#EBF5FF] to-[#FDF2F8] flex items-center justify-center overflow-hidden shadow-lg shadow-[#4E9CFF]/10">
              <img
                src={template.mascot}
                alt={template.name}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="mt-5 text-center md:text-left">
              <div className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-[#4E9CFF] mb-2">
                <GenieIcon
                  name="sparkles"
                  size={16}
                  className="w-4 h-4"
                />
                Genie Template
              </div>
              <h2 className="text-xl md:text-2xl font-bold text-genie-text-primary">
                {template.name}
              </h2>
              <p className="text-sm text-genie-text-sub mt-1">{template.tagline}</p>
            </div>
          </div>

          {/* Right column */}
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-genie-text-primary mb-3">
              Deploy {template.name}
            </h1>

            {/* Full description */}
            <p className="text-sm md:text-base text-genie-text-sub mb-6 max-w-2xl leading-relaxed">
              {template.description}
            </p>

            {/* Jobs to be done */}
            {template.jobsToBeDone && template.jobsToBeDone.length > 0 && (
              <div className="mb-8 rounded-xl bg-white border border-[#E5EAF0] p-5">
                <div className="text-xs font-semibold uppercase tracking-wider text-genie-text-sub mb-3">
                  What this Genie will do for you
                </div>
                <ul className="space-y-2">
                  {template.jobsToBeDone.map((job) => (
                    <li
                      key={job}
                      className="text-sm text-genie-text-primary flex items-start gap-2.5"
                    >
                      <div className="mt-1 w-4 h-4 rounded-full bg-gradient-to-br from-[#4E9CFF] to-[#FF4DB8] flex items-center justify-center shrink-0">
                        <Check size={10} className="text-white" strokeWidth={3} />
                      </div>
                      {job}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* What happens when you deploy */}
            <div className="mb-8">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-genie-text-sub mb-3">
                What happens when you deploy
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <NextStepCard
                  genieIcon="sparkles"
                  title="We clone the template"
                  detail="Prompt, voice, conversation starters, and tools all pre-wired in your workspace."
                />
                <NextStepCard
                  genieIcon="palette"
                  title="You add your branding"
                  detail="Name, voice, logo, colours, and how your Genie should sound on your channels."
                />
                <NextStepCard
                  genieIcon="database"
                  title="You point at your knowledge"
                  detail="Connect the docs, FAQs, and product info your Genie should know."
                />
                <NextStepCard
                  genieIcon="share"
                  title="You connect channels"
                  detail="Phone, web widget, email. Pick where your Genie lives."
                />
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button onClick={onBack} variant="outline">
                Back to catalogue
              </Button>
              <Button
                onClick={handleDeploy}
                className="bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] hover:opacity-95 text-white font-semibold px-6 shadow-lg shadow-[#4E9CFF]/30 gap-2"
              >
                <GenieIcon
                  name="sparkles"
                  size={16}
                  className="w-4 h-4 brightness-0 invert"
                />
                Deploy this Genie
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const NextStepCard = ({
  genieIcon,
  title,
  detail,
}: {
  genieIcon: GenieIconName;
  title: string;
  detail: string;
}) => (
  <div className="rounded-xl bg-white border border-[#E5EAF0] p-4 flex items-start gap-3">
    <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#EBF5FF] to-[#FDF2F8] flex items-center justify-center shrink-0">
      <GenieIcon name={genieIcon} size={20} className="w-5 h-5" />
    </div>
    <div>
      <div className="text-sm font-semibold text-genie-text-primary">{title}</div>
      <div className="text-xs text-genie-text-sub mt-0.5">{detail}</div>
    </div>
  </div>
);
